/**
 * Normalize Hubwayz / Mishor Pre-Book response → UI SSOT model.
 * Paths follow docs.hubwayz.com Mishor → Hotels → Pre-Book
 * (content.Services.hotels[].items[]).
 *
 * Fixture-based / documentation-derived — not a guarantee every live
 * response includes every optional field.
 */

function asObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : null;
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function money(node) {
  const o = asObject(node);
  if (!o) return null;
  const amount = Number(o.amount);
  if (!Number.isFinite(amount)) return null;
  const currency = String(o.currency || "").trim() || null;
  return { amount, currency };
}

function stripHtml(raw) {
  const decoded = String(raw || "")
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
  return decoded
    .replace(/<[^>]*>/g, " ")
    .replace(/[^\S\n]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function remarksFromItem(item) {
  const remarks = item?.remarks;
  if (remarks == null) return [];
  if (typeof remarks === "string") {
    const text = stripHtml(remarks);
    return text ? [{ key: "general", text, html: remarks }] : [];
  }
  const obj = asObject(remarks);
  if (!obj) return [];
  return Object.entries(obj)
    .map(([key, val]) => {
      const html = typeof val === "string" ? val : String(val ?? "");
      const text = stripHtml(html);
      return text ? { key: String(key), text, html } : null;
    })
    .filter(Boolean);
}

function normalizePax(paxNode) {
  const pax = asObject(paxNode) || {};
  const adults = Math.max(0, Number(pax.adults) || 0);
  const childrenRaw = pax.children;
  let childAges = [];
  if (Array.isArray(childrenRaw)) {
    childAges = childrenRaw
      .map((c) => {
        if (typeof c === "number" && Number.isFinite(c)) return c;
        if (c && typeof c === "object" && c.age != null) return Number(c.age);
        return null;
      })
      .filter((n) => Number.isFinite(n));
  }
  return {
    adults,
    childrenCount: Array.isArray(childrenRaw) ? childrenRaw.length : 0,
    childAges,
  };
}

function normalizeHotelInfo(hotelNode) {
  const h = asObject(hotelNode) || {};
  const name = String(h.name || "").trim() || null;
  const address = String(h.address || "").trim() || null;
  const city = String(h.city || "").trim() || null;
  const zip = String(h.zip || "").trim() || null;
  const ratingRaw = h.rating != null ? String(h.rating).trim() : "";
  const rating = ratingRaw || null;
  const addressLine = [address, city, zip].filter(Boolean).join(", ") || null;
  return {
    name,
    address,
    city,
    zip,
    rating,
    addressLine,
    phone: h.phone != null ? String(h.phone) : null,
  };
}

function normalizeFrames(cancellation) {
  const cxl = asObject(cancellation);
  if (!cxl) return { type: null, frames: [] };
  const frames = asArray(cxl.frames).map((f) => {
    const frame = asObject(f) || {};
    const penalty = money(frame.penalty);
    return {
      from: frame.from != null ? String(frame.from) : null,
      to: frame.to != null ? String(frame.to) : null,
      penalty,
    };
  });
  return {
    type: cxl.type != null ? String(cxl.type) : null,
    frames,
  };
}

function normalizeRoomItem(item, index) {
  const it = asObject(item) || {};
  const hotel = normalizeHotelInfo(it.hotel);
  const board = String(it.board || "").trim() || null;
  const boardName = String(it.boardName || "").trim() || null;
  const roomName =
    String(it.name || "").trim() ||
    [it.category, it.bedding].filter(Boolean).join(" · ") ||
    null;
  return {
    index,
    roomId:
      it.roomId != null
        ? String(it.roomId)
        : it.room_id != null
          ? String(it.room_id)
          : null,
    hotelId: it.hotelId != null ? it.hotelId : null,
    roomName,
    category: it.category != null ? String(it.category) : null,
    bedding: it.bedding != null ? String(it.bedding) : null,
    board,
    boardName,
    boardLabel: boardName || board || null,
    hotel,
    pax: normalizePax(it.pax),
    remarks: remarksFromItem(it),
  };
}

function normalizeHotelOffer(hotelNode, offerIndex) {
  const h = asObject(hotelNode) || {};
  const rooms = asArray(h.items).map((item, i) => normalizeRoomItem(item, i));
  const netPrice = money(h.netPrice);
  const price = money(h.price);
  const token = String(h.token || "").trim() || null;
  const cancellation = normalizeFrames(h.cancellation);
  const warnings = asArray(h.warnings).map((w) => ({
    code: w?.code != null ? w.code : null,
    message: w?.message != null ? String(w.message) : null,
  }));
  return {
    offerIndex,
    token,
    code: h.code != null ? String(h.code) : null,
    netPrice,
    price,
    cancellation,
    warnings,
    rooms,
  };
}

/**
 * Critical booking fields required to continue toward payment (cert Pre-Book UI).
 */
export function findHubwayzPrebookCriticalMissing(normalized) {
  const missing = [];
  if (!normalized?.offers?.length) {
    missing.push("hotels");
    return missing;
  }
  const offer = normalized.offers[0];
  if (!offer.token) missing.push("token");
  if (!offer.netPrice && !offer.price) missing.push("price");
  if (!offer.rooms.length) missing.push("rooms");
  const firstRoom = offer.rooms[0];
  if (!firstRoom?.hotel?.name) missing.push("hotel.name");
  if (!firstRoom?.roomName) missing.push("room.name");
  return missing;
}

/**
 * @param {unknown} raw — BE `{ ok, data }` or Mishor Pre-Book body
 * @returns {import('./normalizeHubwayzPrebook.js').HubwayzPrebookNormalized}
 */
export function normalizeHubwayzPrebook(raw) {
  const root = asObject(raw) || {};
  // BE wrap: { ok, data } or Mishor body directly
  const data = asObject(root.data) || root;
  const content = asObject(data.content) || data;
  const services = asObject(content.Services) || asObject(content.services) || {};
  const hotelsRaw = asArray(services.hotels);
  const offers = hotelsRaw.map((h, i) => normalizeHotelOffer(h, i));

  const primary = offers[0] || null;
  const rooms = primary ? primary.rooms : [];
  const hotelNames = [
    ...new Set(rooms.map((r) => r.hotel.name).filter(Boolean)),
  ];
  const addressLines = [
    ...new Set(rooms.map((r) => r.hotel.addressLine).filter(Boolean)),
  ];
  const ratings = [
    ...new Set(rooms.map((r) => r.hotel.rating).filter(Boolean)),
  ];
  const allRemarks = rooms.flatMap((r) =>
    r.remarks.map((rem) => ({
      ...rem,
      roomIndex: r.index,
      roomName: r.roomName,
    }))
  );

  const display = {
    hotelName: hotelNames[0] || null,
    hotelNames,
    addressLine: addressLines[0] || null,
    rating: ratings[0] || null,
    roomNames: rooms.map((r) => r.roomName).filter(Boolean),
    roomSummary: rooms
      .map((r, i) => r.roomName || `Room ${i + 1}`)
      .join(" · "),
    boardLabels: rooms.map((r) => r.boardLabel).filter(Boolean),
    boardSummary: [
      ...new Set(rooms.map((r) => r.boardLabel).filter(Boolean)),
    ].join(" · "),
    token: primary?.token || null,
    netPrice: primary?.netPrice || null,
    price: primary?.price || null,
    cancellation: primary?.cancellation || { type: null, frames: [] },
    remarks: allRemarks,
    warnings: primary?.warnings || [],
    roomsCount: rooms.length,
    adultsTotal: rooms.reduce((s, r) => s + (r.pax.adults || 0), 0),
    childrenTotal: rooms.reduce((s, r) => s + (r.pax.childrenCount || 0), 0),
  };

  const criticalMissing = findHubwayzPrebookCriticalMissing({ offers });
  const supplierConfirmed = criticalMissing.length === 0;

  return {
    source: "hubwayz_prebook",
    offers,
    display,
    criticalMissing,
    supplierConfirmed,
    /** Informational only — never treat as Pre-Book SSOT */
    enrichmentAllowed: false,
  };
}

export function formatHubwayzCancellationFrames(cancellation) {
  const cxl = cancellation || { type: null, frames: [] };
  const lines = [];
  if (cxl.type) lines.push(`סוג: ${cxl.type}`);
  for (const f of cxl.frames || []) {
    const penalty =
      f.penalty != null
        ? `${f.penalty.amount} ${f.penalty.currency || ""}`.trim()
        : "—";
    lines.push(`${f.from || "?"} → ${f.to || "?"} · קנס ${penalty}`);
  }
  return lines;
}
